import React, { Component } from 'react';
import ImageGallery, { GalleryImage, FlowShot } from '../Imgpop';
import FeatureCards from '../FeatureCards';
import TechTags from '../TechTags';
import "./style.css"

class CurriculumHub extends Component {
    render() {
        const galleryImages = [
            { src: require('./img/curriculum-hub/laptop.png'), alt: 'ToolMart laptop view', variant: 'laptop', className: 'laptop-view' },
            { src: require('./img/curriculum-hub/mobile.png'), alt: 'ToolMart mobile view', variant: 'mobile', className: 'mobile-view' },
            {
                src: require('./img/curriculum-hub/course-guide.png'),
                title: 'Course Guide pattern',
                alt: 'Course Guide layout with overview, quick links, course tour, and career relevance',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/curriculum-hub/course-model.png'),
                title: 'Course Model Guide',
                alt: 'Course Model Guide assessments page with sidebar navigation and search',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            { src: require('./img/curriculum-hub/toolmart-vendor.png'), alt: 'ToolMart vendor catalog', variant: 'screenshot', className: 'screenshot-view' },
            {
                src: require('./img/curriculum-hub/toolmart-backend_vendor.png'),
                title: 'Tool editor',
                alt: 'WordPress editor for a ToolMart tool with adoption status, functionality, and vendor details',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/curriculum-hub/toolmart-backend_review.png'),
                title: 'Review rubric',
                alt: 'ToolMart review form with integration, SSO, and outcomes rubric fields',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
        ];

        const highlightCards = [
            {
                title: 'Managed Guide Types',
                description: 'Staff publish course, faculty, course model, implementation, and tool-support guides from one WordPress admin area instead of maintaining separate sites.',
            },
            {
                title: 'Reusable Authoring Patterns',
                description: 'Gutenberg block patterns and Advanced Custom Fields give editors a consistent starting structure, then pull in shared quick links, tools, and other embeddable content.',
            },
            {
                title: 'Vendor Courseware Tracking',
                description: 'ToolMart makes adopted, pilot, and retired tools searchable by vendor, feature, category, and functionality, supporting review work that contributed to about $75,000 in annual savings in 2023.',
            },
        ];

        const relatedProjects = [
            {
                id: 'cwe',
                title: 'Center for Writing Excellence',
                description: 'A related University of Phoenix writing-support platform with personalized resources and a custom APA citation generator.',
                cta: 'View the CWE case study →',
            },
        ];

        return (
            <div className='collapsible'>
                <h1>Curriculum Hub</h1>
                <div className="gradient"></div>
                <p className='eyebrow mt-4'>A hub for guides and vendor tools</p>
                <ImageGallery images={galleryImages}>
                    <div className="details">
                        <p>Curriculum Hub is a WordPress plugin I built to give University of Phoenix curriculum staff one place to manage student and faculty facing guides. Custom post types, Gutenberg patterns, and Advanced Custom Fields keep course guides, faculty guides, and course-model documentation consistent while still letting editors reuse shared content.</p>
                        <p>The hub homepage also points staff to ToolMart, the catalog of courseware and integrations. Tools can be filtered, reviewed, and kept current without maintaining a separate website for every vendor. Single Sign-On session roles control which audiences can see staff-only areas of the experience.</p>
                        <div className="row mt-3">
                            <div className='col-sm-7'>
                                <GalleryImage {...galleryImages[0]} index={0} />
                            </div>
                            <div className='col-sm-5'>
                                <GalleryImage {...galleryImages[1]} index={1} />
                            </div>
                        </div>
                        <p className='eyebrow mt-4'>Hub at a Glance</p>
                        <FeatureCards className="mt-4 mb-4" items={highlightCards} />
                        <div className="feature-card">
                            <p className="eyebrow">Managed Guides</p>
                            <p>I grouped course, faculty, course model, content and style, implementation, and tool-support guides under a custom Managed Guides menu with a dedicated editing capability. Each guide type uses its own post type and a matching single template rather than one generic layout.</p>
                            <p>Course and faculty guides start from Gutenberg block patterns I registered for the hub, covering overview, course tour, FAQs, badges, and shared color blocks. Advanced Custom Fields then attach embeddable content: quick links, interactive tools, mission and attendance copy, so editors can update a shared block once and have it appear across the guides that reference it.</p>
                            <p>The Course Model Guide navigation is built from published course-model pages in menu order, so new sections appear in the sidebar as they are added. SearchWP powers in-guide search, and SSO session roles determine whether a visitor can access the staff-facing documentation.</p>
                            <div className="row mt-3">
                                <div className='col-sm-6'>
                                    <FlowShot {...galleryImages[2]} index={2} layout="single" />
                                </div>
                                <div className='col-sm-6'>
                                    <FlowShot {...galleryImages[3]} index={3} layout="single" />
                                </div>
                            </div>
                        </div>
                        <div className="feature-card">
                            <p className="eyebrow">ToolMart Courseware Catalog</p>
                            <p>ToolMart is the hub’s vendor and integration catalog. The public listing queries adopted, pilot, and retired tools and lets staff search by name or filter by vendor, feature, category, and functionality. Students, faculty, and guests are blocked from the staff catalog using the authenticated Phoenix session role.</p>
                            <p>Each tool is a custom post with taxonomies for vendors, features, categories, and integration functionality. Reviews are a related post type with a structured rubric for technical integration, SSO and account management, and outcomes. Saving a review builds a readable title from the date, related tool, review type, and lifecycle so the history stays searchable in WordPress.</p>
                            <GalleryImage {...galleryImages[4]} index={4} />
                            <div className="row mt-3">
                                <div className='col-sm-6'>
                                    <FlowShot {...galleryImages[5]} index={5} layout="single" />
                                </div>
                                <div className='col-sm-6'>
                                    <FlowShot {...galleryImages[6]} index={6} layout="single" />
                                </div>
                            </div>
                        </div>
                        <p className="eyebrow mt-4">My Role</p>
                        <p>I built the Curriculum Hub plugin, including the homepage template, managed-guide administration, Gutenberg patterns, ACF field groups, and custom single templates that staff use to publish and update curriculum resources.</p>
                        <TechTags
                            items={['WordPress', 'PHP', 'jQuery', 'CSS', 'Bootstrap 5', 'Gutenberg Block Patterns', 'Advanced Custom Fields', 'Custom post types', 'SearchWP', 'SSO session roles', 'WCAG']}
                        />
                        <p className="eyebrow mt-4">Related Projects</p>
                        <FeatureCards className="mt-4 mb-4" items={relatedProjects} onNavigate={this.props.onNavigate} />
                    </div>
                </ImageGallery>
            </div>
        )
    }
}

export default CurriculumHub;
